/**
 * Client Session Hook & Context
 * Manages authentication state for the athlete portal (separate from agency staff)
 */

import { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react'
import {
  getClientSession,
  clearClientAuth,
  refreshClientUser,
  isClientAuthenticated,
} from '../api/clientAuth'
import { isPreview, exitPreview } from '../api/clientPortal/previewMode'
import { unsubscribePortalPush } from '../api/clientPortal/pushSubscriptions'
import type { ClientUser } from '../types/clientPortal'

interface ClientSessionContextValue {
  user: ClientUser | null
  loading: boolean
  isAuthenticated: boolean
  logout: () => void
  refresh: () => Promise<void>
}

const ClientSessionContext = createContext<ClientSessionContextValue | null>(null)

interface ClientSessionProviderProps {
  children: ReactNode
}

export function ClientSessionProvider({ children }: ClientSessionProviderProps) {
  const [user, setUser] = useState<ClientUser | null>(null)
  const [loading, setLoading] = useState(true)

  // Initialize session from storage
  useEffect(() => {
    const session = getClientSession()
    if (session) {
      setUser(session.user)
    }
    setLoading(false)
  }, [])

  // Cross-tab logout sync
  useEffect(() => {
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'client_portal_logout' && e.newValue) {
        setUser(null)
      }
      // Another tab logged in — pick up the session
      if (e.key === 'pocketbase_client_auth' && e.newValue) {
        const session = getClientSession()
        if (session) {
          setUser(session.user)
        }
      }
      // Another tab cleared auth
      if (e.key === 'pocketbase_client_auth' && !e.newValue) {
        setUser(null)
      }
    }

    window.addEventListener('storage', handleStorageChange)
    return () => window.removeEventListener('storage', handleStorageChange)
  }, [])

  // Logout function
  const logout = useCallback(async () => {
    // While previewing there is no portal session to end — leaving the
    // preview IS the way out, and clearing portal auth would only strand the
    // agent on the athlete's sign-in page.
    if (isPreview()) {
      exitPreview()
      setUser(null)
      return
    }
    // This device stops receiving the athlete's notifications before the
    // session goes: otherwise the next person on the phone gets their deal and
    // payment alerts (Oct 2026 audit). Needs the session, so it comes first.
    // A failure is reported, and sign-out still happens.
    const push = await unsubscribePortalPush()
    if (!push.success) console.error('[portal] signing out: notifications could not be switched off on this device:', push.error)
    clearClientAuth()
    setUser(null)
  }, [])

  // Refresh user data
  const refresh = useCallback(async () => {
    try {
      const updated = await refreshClientUser()
      if (updated) {
        setUser(updated)
      } else {
        // Session expired or invalid
        setUser(null)
      }
    } catch (err: any) {
      console.error('Client session refresh failed:', err)
      const status = err?.status || err?.response?.status
      if (status === 401 || status === 403) {
        // Auth expired/invalid — force logout
        setUser(null)
        clearClientAuth()
      }
      // Network errors: keep current session, don't crash
    }
  }, [])

  const value: ClientSessionContextValue = {
    user,
    loading,
    isAuthenticated: !!user,
    logout,
    refresh,
  }

  return (
    <ClientSessionContext.Provider value={value}>
      {children}
    </ClientSessionContext.Provider>
  )
}

/**
 * Hook to access the client session context
 */
export function useClientSession(): ClientSessionContextValue {
  const context = useContext(ClientSessionContext)
  if (!context) {
    throw new Error('useClientSession must be used within a ClientSessionProvider')
  }
  return context
}

/**
 * Hook for checking if client is authenticated
 * Can be used outside of provider for simple checks
 */
export function useClientAuth(): boolean {
  return isClientAuthenticated()
}
